/**
 * Comment density rail (F5c) — a vertical strip beside the canvas showing
 * WHERE the feedback is on this page, ported from the CRM design-reviewer's
 * `.dr-rev-density-rail`.
 *
 * It re-measures nothing. `comments:pin-positions` republishes the exact y a
 * pin landed on, so a dot and its pin cannot disagree and no second rect
 * round-trip is spent — the same "compute it once" rule that governs thread
 * numbers.
 *
 * Two deliberate departures from the reference:
 *  • The CRM does NO collision handling: N annotations at one y render as N
 *    stacked circles and only the topmost is clickable. Nearby dots here
 *    CLUSTER into a single counted badge that steps through its members.
 *  • Only ACTIVE-PAGE threads have a y at all. Threads on other pages are
 *    real but unplaceable here, so the rail shows a count rather than
 *    pretending they sit somewhere on this page.
 */

import { eventBus } from '../core/events.js';
import { getCommentThreads, getActiveCommentThread } from './comment-controller.js';

/** Dots closer than this share a badge. Percent of the rail's height. */
const CLUSTER_PCT = 1.6;

/**
 * Group dots that would overlap. Input must be sorted by `pct` ascending.
 * A cluster keeps every member so a repeat click steps through them, and is
 * drawn at the mean of its members rather than at the first one's position.
 *
 * @param {Array<{threadId: string, pct: number, status: string}>} dots
 * @param {number} [threshold=CLUSTER_PCT]
 * @returns {Array<{pct: number, members: string[], hasOpen: boolean}>}
 */
export function clusterDots(dots, threshold = CLUSTER_PCT) {
  const out = [];
  for (const dot of dots) {
    const last = out[out.length - 1];
    if (last && dot.pct - last.pct <= threshold) {
      last.members.push(dot.threadId);
      last.hasOpen = last.hasOpen || dot.status !== 'resolved';
      // Mean keeps a cluster centred on what it represents; anchoring to the
      // first member would drift the badge upward as members accumulate.
      last._sum += dot.pct;
      last.pct = last._sum / last.members.length;
      continue;
    }
    out.push({
      pct: dot.pct,
      _sum: dot.pct,
      members: [dot.threadId],
      hasOpen: dot.status !== 'resolved'
    });
  }
  return out.map(({ pct, members, hasOpen }) => ({ pct, members, hasOpen }));
}

/**
 * Convert published pin positions into clustered rail dots.
 * @returns {Array<{pct: number, members: string[], hasOpen: boolean}>}
 */
export function railDotsFrom(positions, contentHeight, threshold = CLUSTER_PCT) {
  if (!Array.isArray(positions) || !(contentHeight > 0)) return [];
  const dots = positions
    .map((p) => ({
      threadId: p.threadId,
      status: p.status,
      pct: Math.max(0, Math.min(100, (p.y / contentHeight) * 100))
    }))
    .sort((a, b) => a.pct - b.pct);
  return clusterDots(dots, threshold);
}

/** The viewport band: where the visible slice sits within the whole page. */
export function viewportBand(scrollY, viewportHeight, contentHeight) {
  if (!(contentHeight > 0) || !(viewportHeight > 0)) return null;
  const top = Math.max(0, Math.min(100, (scrollY / contentHeight) * 100));
  // Floor the height so a very long page still shows a grabbable band.
  const height = Math.max(2, Math.min(100 - top, (viewportHeight / contentHeight) * 100));
  return { top, height };
}

/**
 * Mount the rail. Returns a handle; the comments panel owns its lifetime.
 * @param {{onActivate: (threadId: string) => void}} deps
 */
export function mountCommentRail({ onActivate } = {}) {
  const rail = document.createElement('div');
  rail.className = 'pb-comment-rail';
  rail.setAttribute('role', 'group');
  rail.setAttribute('aria-label', 'Comment positions on this page');

  const band = document.createElement('div');
  band.className = 'pb-comment-rail-band';
  band.hidden = true;
  rail.appendChild(band);

  const dotsWrap = document.createElement('div');
  dotsWrap.className = 'pb-comment-rail-dots';
  rail.appendChild(dotsWrap);

  const offPage = document.createElement('span');
  offPage.className = 'pb-comment-rail-offpage';
  offPage.hidden = true;
  rail.appendChild(offPage);

  document.body.appendChild(rail);

  let _clusters = [];
  const _stepIndex = new Map(); // cluster key → which member is next

  function paint({ positions = [], contentHeight = 0, viewportHeight = 0, scrollY = 0 } = {}) {
    _clusters = railDotsFrom(positions, contentHeight);
    const activeId = getActiveCommentThread();

    dotsWrap.textContent = '';
    for (const cluster of _clusters) {
      // pb-sweep-exempt: no-hand-rolled-button — a rail dot is positioned chrome (percent-placed, count-labeled), not a kit action button; it carries its own aria-label and has no busy/async lifecycle
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pb-comment-rail-dot';
      dot.style.top = `${cluster.pct}%`;
      dot.classList.toggle('is-resolved', !cluster.hasOpen);
      dot.classList.toggle('is-active', cluster.members.includes(activeId));
      if (cluster.members.length > 1) {
        dot.classList.add('is-cluster');
        dot.textContent = String(cluster.members.length);
      }
      dot.setAttribute(
        'aria-label',
        cluster.members.length > 1
          ? `${cluster.members.length} comments here — activate to step through them`
          : `Comment ${cluster.hasOpen ? 'open' : 'resolved'}`
      );
      dot.addEventListener('click', () => {
        // Repeat clicks walk a cluster instead of re-selecting its first member.
        const key = cluster.members.join(',');
        const next = (_stepIndex.get(key) || 0) % cluster.members.length;
        _stepIndex.set(key, next + 1);
        onActivate?.(cluster.members[next]);
      });
      dotsWrap.appendChild(dot);
    }

    const bandRect = viewportBand(scrollY, viewportHeight, contentHeight);
    band.hidden = !bandRect;
    if (bandRect) {
      band.style.top = `${bandRect.top}%`;
      band.style.height = `${bandRect.height}%`;
    }

    // Threads elsewhere in the project are real but have no position here.
    const placed = new Set(positions.map((p) => p.threadId));
    const elsewhere = getCommentThreads().filter((t) => !placed.has(t.id)).length;
    offPage.hidden = elsewhere === 0;
    offPage.textContent = elsewhere ? `+${elsewhere}` : '';
    if (elsewhere) {
      offPage.dataset.tooltip = `${elsewhere} comment${elsewhere === 1 ? '' : 's'} on other pages or without a position here`;
    } else {
      delete offPage.dataset.tooltip;
    }

    rail.hidden = _clusters.length === 0 && elsewhere === 0;
  }

  const offs = [
    eventBus.on('comments:pin-positions', paint),
    eventBus.on('comments:active-thread', ({ threadId } = {}) => {
      for (let i = 0; i < dotsWrap.children.length; i++) {
        const cluster = _clusters[i];
        dotsWrap.children[i].classList.toggle(
          'is-active',
          !!threadId && !!cluster && cluster.members.includes(threadId)
        );
      }
    })
  ];

  paint(); // start hidden until positions arrive

  return {
    destroy() {
      for (const off of offs) if (typeof off === 'function') off();
      rail.remove();
    }
  };
}
